import { NotificationService } from '@agilent/ui-element';
import { Injectable, OnDestroy } from '@angular/core';
import { AutoLogoutComponent } from '@app/pages/auto-logout/auto-logout.component';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '@app/user.service';

const TEN_MINUTES = 10 * 60 * 1000; // in milliseconds
const CHECK_INTERVAL = 15000; // in ms
const STORE_KEY = 'lastAction.timestamp';
const CONTINUE_SESSION_STORE_KEY = 'lastAction.continueSession';
const THIRTY_SECONDS = 30 * 1000; // in milliseconds

@Injectable({ providedIn: 'root' })
export class AutoLogoutService implements OnDestroy {

    private isDialogVisible = false;
    private autoLogoutDialog: NgbModalRef;
    private countdownInterval;
    private sessionTimeoutInterval;
    private isLoggingOut = false;
    private dialogInfo = {
        countdown: 30
    };

    constructor(
        private userService: UserService,
        private notificationService: NotificationService
    ) {
        //
    }

    initService() {
        this.resetLastUiActionTimestamp();
        this.initUiActionListeners();
        this.initSessionTimeoutInterval();
    }

    resetLastUiActionTimestamp() {
        if (this.isCountdownStarted()) {
            return this.checkSessionTimeout();
        }
        this.setLastUiActionTimestamp(Date.now());
    }

    initUiActionListeners() {
        document.body.addEventListener('click', () => this.resetLastUiActionTimestamp());
        document.body.addEventListener('mouseover', () => this.resetLastUiActionTimestamp());
        document.body.addEventListener('mouseout', () => this.resetLastUiActionTimestamp());
        document.body.addEventListener('keydown', () => this.resetLastUiActionTimestamp());
        document.body.addEventListener('keyup', () => this.resetLastUiActionTimestamp());
        document.body.addEventListener('keypress', () => this.resetLastUiActionTimestamp());
        document.addEventListener('visibilitychange', () => this.checkSessionTimeout());
    }

    initSessionTimeoutInterval() {
        this.checkSessionTimeout();
        this.sessionTimeoutInterval = setInterval(() => this.checkSessionTimeout(), CHECK_INTERVAL);
    }

    checkSessionTimeout() {
        const timeleft = this.getLastUiActionTimestamp() + TEN_MINUTES;
        const diff = timeleft - Date.now();
        const isTimeout = diff < 0;

        if (!isTimeout) {
            return;
        }
        // If the session timed out more than 30 seconds ago.
        if (Math.abs(diff) > THIRTY_SECONDS) {
            return this.logOutUser(true);
        }
        // Initialize countdown
        this.initCountdown();
    }

    initCountdown() {
        if (this.isDialogVisible) {
            return;
        }
        const countdownStartTime = this.getSavedCountdownStartTime() || Date.now();
        this.setCountdownStartTime(countdownStartTime);
        const countdownEndTime = countdownStartTime + THIRTY_SECONDS;
        const remainingTime = Math.floor((countdownEndTime - Date.now()) / 1000); // seconds

        // Show dialog
        this.showAutoLogoutDialog(remainingTime);

        // Start countdown
        this.countdownInterval = setInterval(() => {
            // If "Continue" was clicked from another tab
            if (this.isContinueSession()) {
                return this.autoLogoutDialog.dismiss();
            }
            // Countdown has expired
            if (this.dialogInfo.countdown < 1) {
                return this.autoLogoutDialog.close({ answer: 'Expired' });
            }
            // Decrement & continue
            this.dialogInfo.countdown--;
        }, 1000);
    }

    showAutoLogoutDialog = async (countdown) => {
        this.removeContinueSessionFlag();
        this.isDialogVisible = true;
        this.dialogInfo.countdown = countdown;
        this.autoLogoutDialog = this.notificationService.showModal(AutoLogoutComponent, {
            modalInfo: this.dialogInfo
        });
        try {
            const result = await this.autoLogoutDialog.result;
            if (result.answer === 'Logout') {
                return this.logOutUser();
            }
            if (result.answer === 'Expired') {
                return this.logOutUser(true);
            }
        }
        catch (reason) {
            // Continue button via escape key.
        }
        finally {
            this.isDialogVisible = false;
            this.setLastUiActionTimestamp(Date.now());
            this.setContinueSessionFlag(true);
            this.stopCountdown();
        }
    }

    stopCountdown() {
        clearInterval(this.countdownInterval);
        this.removeCountdownStartTime();
    }

    getLastUiActionTimestamp() {
        return parseInt(localStorage.getItem(STORE_KEY), 10);
    }

    setLastUiActionTimestamp(lastAction: number) {
        localStorage.setItem(STORE_KEY, lastAction.toString());
    }

    getSavedCountdownStartTime() {
        return Number(localStorage.getItem('autoLogoutStartTime'));
    }

    setCountdownStartTime(seconds) {
        localStorage.setItem(String('autoLogoutStartTime'), seconds);
    }

    removeCountdownStartTime() {
        localStorage.removeItem('autoLogoutStartTime');
    }

    setContinueSessionFlag(continueSession: boolean) {
        localStorage.setItem(CONTINUE_SESSION_STORE_KEY, String(continueSession));
    }

    isContinueSession() {
        return Boolean(localStorage.getItem(CONTINUE_SESSION_STORE_KEY));
    }

    removeContinueSessionFlag() {
        localStorage.removeItem(CONTINUE_SESSION_STORE_KEY);
    }

    isCountdownStarted() {
        return !!this.getSavedCountdownStartTime();
    }

    async logOutUser(isTimeout: boolean = false) {
        if (this.isLoggingOut) {
            return;
        }
        this.isLoggingOut = true;
        clearInterval(this.sessionTimeoutInterval);
        this.stopCountdown();
        return this.userService.logOutUser(isTimeout);
    }

    ngOnDestroy(): void {
        clearInterval(this.sessionTimeoutInterval);
        this.removeCountdownStartTime();
    }
}
